import React from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  ArrowRight, Code2, Search, Plug, Zap, Users, Target, Award, TrendingUp, Quote, Star, Globe2, Sparkles
} from "lucide-react";
import ContactForm from "../components/ContactForm";
import DomainSearch from "../components/DomainSearch";
import HomeAIBuilder from "../components/HomeAIBuilder";

/**
 * Floating decorative icons in hero — themed for our 4 service pillars:
 *  • Website Design (code/laptop)
 *  • App Development (mobile/phone)
 *  • SEO (search/chart)
 *  • AI (brain/sparkle/bot)
 * Each icon floats with a unique animation timing.
 */
const HERO_ICONS = [
  // Website design — Code brackets
  { top: "12%", left: "8%",  size: 130, delay: 0,   duration: 9,  to: "/services#website-design", label: "Website Design",
    svg: <><path d="M16 18l6-6-6-6"/><path d="M8 6l-6 6 6 6"/><path d="M14 4l-4 16"/></> },
  // Website design — Layout/Window
  { top: "62%", left: "6%",  size: 110, delay: 1.2, duration: 11, to: "/services#website-design", label: "Website Layouts",
    svg: <><rect x="3" y="3" width="18" height="18" rx="2"/><path d="M3 9h18"/><path d="M9 21V9"/></> },
  // App development — Smartphone
  { top: "18%", left: "78%", size: 120, delay: 0.6, duration: 10, to: "/ai-app-builder", label: "App Development",
    svg: <><rect x="6" y="2" width="12" height="20" rx="3"/><path d="M11 18h2"/></> },
  // App development — App tiles
  { bottom: "20%", left: "76%", size: 100, delay: 2.0, duration: 12, to: "/ai-app-builder", label: "Build a SaaS App",
    svg: <><rect x="3" y="3" width="7" height="7" rx="1"/><rect x="14" y="3" width="7" height="7" rx="1"/><rect x="3" y="14" width="7" height="7" rx="1"/><rect x="14" y="14" width="7" height="7" rx="1"/></> },
  // SEO — magnifying glass
  { top: "30%", left: "20%", size: 100, delay: 0.4, duration: 9.5, to: "/services#seo", label: "SEO Optimization",
    svg: <><circle cx="11" cy="11" r="7"/><path d="m21 21-4.3-4.3"/></> },
  // SEO — bar chart
  { bottom: "14%", left: "22%", size: 110, delay: 1.8, duration: 10.5, to: "/services#seo", label: "SEO Analytics",
    svg: <><path d="M3 3v18h18"/><path d="M7 16l4-8 4 4 5-9"/></> },
  // SEO — trending up
  { top: "70%", left: "40%", size: 90, delay: 2.4, duration: 11, to: "/services#seo", label: "Grow Rankings",
    svg: <><path d="M3 17l6-6 4 4 8-8"/><path d="M17 7h4v4"/></> },
  // AI — sparkles
  { top: "8%",  left: "44%", size: 90, delay: 0.9, duration: 8, to: "/ai-builder", label: "AI Website Builder",
    svg: <><path d="M12 3v3M12 18v3M3 12h3M18 12h3M5.6 5.6l2.1 2.1M16.3 16.3l2.1 2.1M5.6 18.4l2.1-2.1M16.3 7.7l2.1-2.1"/><circle cx="12" cy="12" r="3"/></> },
  // AI — brain/circuit
  { top: "50%", left: "62%", size: 130, delay: 1.5, duration: 9.5, to: "/ai-tools", label: "AI Tools Suite",
    svg: <><path d="M9.5 2a3 3 0 0 1 3 3v14a3 3 0 0 1-6 0V8a3 3 0 0 1 3-3z" transform="translate(2 0)"/><path d="M14.5 2a3 3 0 0 0-3 3v14a3 3 0 0 0 6 0V8a3 3 0 0 0-3-3z" transform="translate(2 0)"/></> },
  // AI — chip
  { bottom: "32%", left: "52%", size: 105, delay: 2.6, duration: 10, to: "/ai-tools", label: "AI Chatbot",
    svg: <><rect x="4" y="4" width="16" height="16" rx="2"/><rect x="9" y="9" width="6" height="6"/><path d="M9 1v3M15 1v3M9 20v3M15 20v3M1 9h3M1 15h3M20 9h3M20 15h3"/></> },
];

const HeroDecor = () => (
  <div className="hero-icons" aria-hidden="true">
    {HERO_ICONS.map((it, i) => (
      <Link
        key={i}
        to={it.to}
        title={it.label}
        aria-label={it.label}
        data-testid={`hero-icon-${i}`}
        className="hero-icon-link group"
        style={{
          position: "absolute",
          top: it.top,
          left: it.left,
          bottom: it.bottom,
          right: it.right,
          width: it.size,
          height: it.size,
        }}
      >
        <motion.svg
          width={it.size}
          height={it.size}
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.4"
          strokeLinecap="round"
          strokeLinejoin="round"
          initial={{ opacity: 0, y: 20, rotate: -8 }}
          animate={{
            opacity: 1,
            y: [0, -18, 0],
            rotate: [-3, 3, -3],
          }}
          transition={{
            delay: it.delay,
            duration: it.duration,
            repeat: Infinity,
            ease: "easeInOut",
          }}
        >
          {it.svg}
        </motion.svg>
        <span className="hero-icon-tooltip">{it.label}</span>
      </Link>
    ))}
  </div>
);

const SERVICES = [
  { icon: Code2, title: "Website Design & Development", desc: "Professional, responsive websites that convert visitors into customers.", to: "/services#website-design" },
  { icon: Search, title: "SEO Optimization", desc: "Rank higher on Google and drive organic traffic to your website.", to: "/services#seo" },
  { icon: Plug,   title: "API Integration", desc: "Seamlessly connect your systems and automate data flow.", to: "/services#api-integration" },
  { icon: Zap,    title: "Business Automation", desc: "Automate repetitive tasks and focus on growing your business.", to: "/services#automation" },
];

const STATS = [
  { icon: Users,       value: "166+", label: "Projects Completed" },
  { icon: Target,      value: "98%",  label: "Client Satisfaction" },
  { icon: Award,       value: "3+",   label: "Years Experience" },
  { icon: TrendingUp,  value: "16+",  label: "Team Members" },
];

const TESTIMONIALS = [
  {
    quote: "Optivize.digital transformed our online presence completely. Their SEO strategies increased our organic traffic by 300% in just 6 months. Highly recommended!",
    name: "Rajesh Kumar", role: "CEO at TechStart Solutions",
    avatar: "https://images.unsplash.com/photo-1560250097-0b93528c311a?w=200&h=200&fit=crop&crop=faces",
  },
  {
    quote: "The team at Optivize.digital is exceptional. They built us a beautiful e-commerce website and managed our digital marketing campaigns. Our sales have tripled!",
    name: "Priya Sharma", role: "Marketing Director at Fashion Hub",
    avatar: "https://images.unsplash.com/photo-1573496359142-b8d87734a5a2?w=200&h=200&fit=crop&crop=faces",
  },
  {
    quote: "Their API integration services saved us thousands of hours of manual work. The automation they implemented is running flawlessly. Great team to work with!",
    name: "Amit Patel", role: "Founder at EduTech Pro",
    avatar: "https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=200&h=200&fit=crop&crop=faces",
  },
];

export default function Landing() {
  return (
    <div>
      {/* HERO — light/dark MIXED theme. Left column on a soft cream glass panel
          so the navy "Optivize" wordmark reads crisply. Right column stays on
          the dark navy gradient with the white tagline + CTAs. Below: a light
          service strip + the dark quote pill — a true two-tone composition. */}
      <section className="hero-blue hero-dark-bg relative overflow-hidden" data-testid="hero-section">
        <HeroDecor />
        {/* Animated background layer: soft floating gradient blobs + dot grid */}
        <div className="absolute inset-0 pointer-events-none" aria-hidden="true">
          <div className="absolute top-[8%] left-[2%] w-[420px] h-[420px] rounded-full opacity-25 blur-3xl"
               style={{background:"radial-gradient(circle, #3B82F6 0%, transparent 60%)", animation:"float-y 9s ease-in-out infinite"}}/>
          <div className="absolute bottom-[10%] right-[5%] w-[380px] h-[380px] rounded-full opacity-25 blur-3xl"
               style={{background:"radial-gradient(circle, #1E3A8A 0%, transparent 60%)", animation:"float-y 11s ease-in-out infinite 1s"}}/>
          <div className="absolute top-[40%] left-[55%] w-[280px] h-[280px] rounded-full opacity-15 blur-3xl"
               style={{background:"radial-gradient(circle, #60A5FA 0%, transparent 60%)", animation:"float-y 13s ease-in-out infinite 2s"}}/>
          <div className="absolute inset-0 opacity-25"
               style={{backgroundImage:"radial-gradient(rgba(96,165,250,0.18) 1.2px, transparent 1.2px)", backgroundSize:"28px 28px"}}/>
          <svg className="absolute top-8 right-8 w-32 h-32 opacity-30" viewBox="0 0 100 100" fill="none">
            <motion.path d="M10 90 L90 10 M90 10 L60 10 M90 10 L90 40" stroke="#60A5FA" strokeWidth="2.5" strokeLinecap="round"
              initial={{pathLength:0, opacity:0}} animate={{pathLength:1, opacity:1}} transition={{duration:2.5, repeat:Infinity, repeatDelay:3, ease:"easeInOut"}}/>
          </svg>
          {/* SMART SOLUTIONS · STRONGER BUSINESSES · LIMITLESS GROWTH — top-right corner */}
          <motion.div
            initial={{opacity:0, x:30}} animate={{opacity:1, x:0}} transition={{duration:.8, delay:1.3}}
            className="hidden lg:block absolute top-24 right-12 px-5 py-3 border border-cyan-400/40 rounded-md bg-blue-950/30 backdrop-blur-sm"
            data-testid="hero-smart-solutions-tag"
            style={{clipPath:"polygon(8% 0, 100% 0, 100% 100%, 0 100%)"}}
          >
            <div className="text-right text-xs sm:text-sm font-bold tracking-wider" style={{fontFamily:"Outfit"}}>
              <div><span className="text-white">SMART </span><span className="text-cyan-400">SOLUTIONS.</span></div>
              <div><span className="text-white">STRONGER </span><span className="text-cyan-400">BUSINESSES.</span></div>
              <div><span className="text-white">LIMITLESS </span><span className="text-cyan-400">GROWTH.</span></div>
            </div>
          </motion.div>
        </div>

        <div className="relative max-w-7xl mx-auto px-5 pt-16 pb-20 lg:pt-20 lg:pb-24">
          <div className="grid lg:grid-cols-12 gap-10 items-center">
            {/* LEFT: huge WHITE logo on the dark navy — matches brand reference */}
            <motion.div
              initial={{opacity:0, x:-30}} animate={{opacity:1, x:0}} transition={{duration:.8}}
              className="lg:col-span-7 text-center lg:text-left"
              data-testid="hero-logo-panel"
            >
              <motion.img
                src="/optivize-logo-white.png"
                alt="Optivize — DBA of Surajit Majhi HUF"
                className="w-full max-w-[640px] mx-auto lg:mx-0 h-auto drop-shadow-[0_8px_40px_rgba(96,165,250,0.4)]"
                initial={{opacity:0, scale:.92}} animate={{opacity:1, scale:1}}
                transition={{duration:1, delay:.1, ease:"easeOut"}}
                style={{animation:"float-y 8s ease-in-out infinite 2s"}}
                data-testid="hero-logo-img"
              />
              <motion.div
                initial={{opacity:0, y:14}} animate={{opacity:1, y:0}} transition={{duration:.7, delay:.5}}
                className="mt-4 flex items-center justify-center lg:justify-start gap-3 text-cyan-300 tracking-[0.18em] text-sm sm:text-base font-semibold"
              >
                <div className="h-px w-10 bg-cyan-400/70"/>
                <span className="text-white/90">DBA OF <span className="text-cyan-300">SURAJIT MAJHI HUF</span></span>
              </motion.div>

              {/* INNOVATE · OPTIMIZE · GROW pillars */}
              <motion.div
                initial={{opacity:0}} animate={{opacity:1}} transition={{duration:.9, delay:.9}}
                className="mt-10 flex items-center justify-center lg:justify-start gap-6 sm:gap-8 flex-wrap"
                data-testid="hero-pillars"
              >
                {[
                  {label:"INNOVATE", svg:<><path d="M14 9l3 3-3 3"/><path d="M5 16c-2-2-2-7 0-9 2-2 7-2 9 0"/><path d="M19 8c2 2 2 7 0 9-2 2-7 2-9 0"/></>, color:"#60A5FA"},
                  {label:"OPTIMIZE", svg:<><circle cx="12" cy="12" r="9"/><circle cx="12" cy="12" r="5"/><circle cx="12" cy="12" r="1"/></>, color:"#22D3EE"},
                  {label:"GROW",     svg:<><path d="M3 17l6-6 4 4 8-8"/><path d="M17 7h4v4"/></>, color:"#93C5FD"},
                ].map((p, idx) => (
                  <div key={p.label} className="flex items-center gap-2.5">
                    <motion.svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke={p.color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"
                      animate={{rotate:[0,8,-8,0]}} transition={{duration:4, repeat:Infinity, delay:idx*0.4}}>
                      {p.svg}
                    </motion.svg>
                    <span className="text-white font-bold tracking-wider text-sm sm:text-base" style={{fontFamily:"Outfit"}}>{p.label}</span>
                    {idx < 2 && <span className="text-cyan-500/60 mx-1">|</span>}
                  </div>
                ))}
              </motion.div>
            </motion.div>

            {/* RIGHT: dark side — tagline + CTAs on the navy gradient */}
            <motion.div
              initial={{opacity:0, x:30}} animate={{opacity:1, x:0}} transition={{duration:.8, delay:.2}}
              className="lg:col-span-5 text-center lg:text-left"
            >
              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-extrabold leading-[1.05] tracking-tight" style={{fontFamily:"Outfit"}}>
                <motion.span initial={{opacity:0, y:18}} animate={{opacity:1, y:0}} transition={{delay:.35}} className="block">
                  <span className="text-white">WE </span><span className="text-white">DESIGN.</span>
                </motion.span>
                <motion.span initial={{opacity:0, y:18}} animate={{opacity:1, y:0}} transition={{delay:.55}} className="block">
                  <span className="text-white">WE </span><span style={{color:"#3B82F6"}}>DEVELOP.</span>
                </motion.span>
                <motion.span initial={{opacity:0, y:18}} animate={{opacity:1, y:0}} transition={{delay:.75}} className="block">
                  <span className="text-white">WE </span><span style={{color:"#60A5FA"}}>GROW.</span>
                </motion.span>
              </h1>

              <motion.div initial={{opacity:0}} animate={{opacity:1}} transition={{delay:1}}
                className="mt-7 border-l-2 border-blue-400/60 pl-4 text-blue-100/85 leading-relaxed max-w-lg mx-auto lg:mx-0">
                Innovative solutions that <span className="text-white font-semibold">automate</span>,
                <span className="text-white font-semibold"> streamline</span> and
                <span className="text-white font-semibold"> scale</span> your business in the digital world.
              </motion.div>

              <motion.div initial={{opacity:0, y:14}} animate={{opacity:1, y:0}} transition={{delay:1.15}}
                className="mt-8 flex flex-wrap gap-4 justify-center lg:justify-start">
                <Link to="/contact" className="btn-primary" data-testid="hero-cta-consult">
                  Get Free Consultation <ArrowRight size={16}/>
                </Link>
                <Link to="/services" className="btn-outline" data-testid="hero-cta-services">
                  View Services
                </Link>
              </motion.div>
            </motion.div>
          </div>

          {/* Service icon strip — DARK band with bordered card matching the reference */}
          <motion.div
            initial="hidden" whileInView="visible" viewport={{once:true}}
            variants={{hidden:{}, visible:{transition:{staggerChildren:0.06, delayChildren:0.4}}}}
            className="mt-16 lg:mt-20 rounded-2xl px-6 py-8 lg:py-10 border border-cyan-400/30 bg-blue-950/40 backdrop-blur-sm relative"
            data-testid="hero-service-strip"
          >
            <div className="absolute -top-3 left-6 px-4 py-1 rounded-md text-[11px] tracking-[0.2em] font-bold text-white"
                 style={{background:"linear-gradient(90deg, #1D4ED8 0%, #06B6D4 100%)"}}>
              OUR SERVICES
            </div>
            <div className="grid grid-cols-3 sm:grid-cols-5 lg:grid-cols-9 gap-4 sm:gap-5">
            {[
              {label:"WEBSITE DESIGN",       to:"/services#website-design", svg:<><rect x="3" y="4" width="18" height="13" rx="2"/><path d="M8 21h8"/><path d="M12 17v4"/><path d="M6 8h6M6 11h4"/></>},
              {label:"WEB DEVELOPMENT",      to:"/services#website-design", svg:<><path d="M16 18l6-6-6-6"/><path d="M8 6l-6 6 6 6"/></>},
              {label:"AI AUTOMATION & INTEGRATION", to:"/ai-tools", svg:<><rect x="5" y="7" width="14" height="11" rx="3"/><circle cx="9" cy="12" r="1.2"/><circle cx="15" cy="12" r="1.2"/><path d="M12 3v4M9 21h6"/></>},
              {label:"SEO & DIGITAL MARKETING", to:"/services#seo", svg:<><circle cx="11" cy="11" r="7"/><path d="m20 20-4.3-4.3"/><path d="M7 13l2-2 1.5 1.5L13 10"/></>},
              {label:"API INTEGRATION",      to:"/services#api-integration", svg:<><circle cx="12" cy="12" r="3"/><path d="M12 3v4M12 17v4M3 12h4M17 12h4"/></>},
              {label:"BRANDING & GRAPHIC DESIGN", to:"/services", svg:<><path d="M12 19l7-7 3 3-7 7-3-3z"/><path d="M18 13l-1.5-7.5L2 2l3.5 14.5L13 18l5-5z"/><circle cx="6" cy="6" r="1.5"/></>},
              {label:"E-COMMERCE SOLUTIONS", to:"/services", svg:<><circle cx="9" cy="20" r="1.5"/><circle cx="18" cy="20" r="1.5"/><path d="M2 4h3l3 12h11l2-8H6"/></>},
              {label:"CLOUD & DEPLOYMENT",   to:"/ai-tools", svg:<><path d="M17.5 19a4.5 4.5 0 1 0-1.4-8.8 6 6 0 0 0-11.7 1.8A4 4 0 0 0 6 19h11.5z"/></>},
              {label:"WORKFLOW AUTOMATION",  to:"/services#automation", svg:<><circle cx="6" cy="6" r="2.5"/><circle cx="18" cy="6" r="2.5"/><circle cx="6" cy="18" r="2.5"/><circle cx="18" cy="18" r="2.5"/><path d="M8.5 6h7M6 8.5v7M18 8.5v7M8.5 18h7"/></>},
            ].map((s) => (
              <motion.div
                key={s.label}
                variants={{hidden:{opacity:0, y:18}, visible:{opacity:1, y:0}}}
                whileHover={{y:-6, scale:1.05}}
                className="flex flex-col items-center text-center"
                data-testid={`service-pill-${s.label.toLowerCase().replace(/[^a-z0-9]+/g,"-")}`}
              >
                <Link to={s.to} className="relative w-16 h-16 sm:w-[68px] sm:h-[68px] flex items-center justify-center group transition-transform">
                  <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="#bfdbfe" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"
                       className="group-hover:stroke-cyan-300 transition-colors drop-shadow-[0_0_8px_rgba(96,165,250,0.4)]">
                    {s.svg}
                  </svg>
                </Link>
                <div className="mt-2 text-[10px] sm:text-[11px] text-blue-100/90 font-bold tracking-wider leading-tight" style={{fontFamily:"Outfit"}}>
                  {s.label}
                </div>
              </motion.div>
            ))}
            </div>
          </motion.div>

          {/* 4 VALUE-PROP PILLAR CARDS — matches the reference image */}
          <motion.div
            initial="hidden" whileInView="visible" viewport={{once:true}}
            variants={{hidden:{}, visible:{transition:{staggerChildren:0.1, delayChildren:0.2}}}}
            className="mt-10 rounded-2xl px-6 py-8 border border-cyan-400/25 bg-blue-950/40 backdrop-blur-sm grid sm:grid-cols-2 lg:grid-cols-4 gap-6"
            data-testid="hero-value-pillars"
          >
            {[
              {title:"INNOVATIVE SOLUTIONS", desc:"We bring innovative ideas to life.", svg:<><path d="M4.5 19.5l2-2M2 22l5.5-5.5"/><path d="M10 14a8 8 0 0 1 8-8 8 8 0 0 0-8 8z"/><path d="M14 10a8 8 0 0 1-8 8 8 8 0 0 0 8-8z"/><path d="M14 10l8-8"/><circle cx="18" cy="6" r="2"/></>},
              {title:"OPTIMIZED PROCESSES", desc:"We optimize for maximum efficiency.", svg:<><circle cx="12" cy="12" r="9"/><circle cx="12" cy="12" r="5"/><circle cx="12" cy="12" r="1.5" fill="#22D3EE" stroke="none"/><path d="M12 3v3M12 18v3M3 12h3M18 12h3"/></>},
              {title:"AUTOMATED SYSTEMS", desc:"We automate tasks to save time & resources.", svg:<><circle cx="12" cy="12" r="9"/><path d="M12 7v5l3 2"/><path d="M16 3l2 2-2 2"/><path d="M8 3L6 5l2 2"/></>},
              {title:"MEASURABLE GROWTH", desc:"We help you scale and achieve more.", svg:<><path d="M3 21h18"/><rect x="5" y="13" width="3" height="6"/><rect x="10.5" y="9" width="3" height="10"/><rect x="16" y="5" width="3" height="14"/><path d="M16 5l-2-2 4-1 0 4z" fill="#22D3EE" stroke="none"/></>},
            ].map((p) => (
              <motion.div
                key={p.title}
                variants={{hidden:{opacity:0, y:20}, visible:{opacity:1, y:0}}}
                whileHover={{y:-4}}
                className="flex flex-col items-start"
                data-testid={`hero-pillar-${p.title.toLowerCase().replace(/\s+/g,"-")}`}
              >
                <svg width="44" height="44" viewBox="0 0 24 24" fill="none" stroke="#60A5FA" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"
                     className="drop-shadow-[0_0_12px_rgba(96,165,250,0.5)]">
                  {p.svg}
                </svg>
                <h3 className="mt-3 text-sm sm:text-base font-bold tracking-wider text-white" style={{fontFamily:"Outfit"}}>{p.title}</h3>
                <p className="mt-1.5 text-xs sm:text-sm text-blue-100/75 leading-relaxed">{p.desc}</p>
              </motion.div>
            ))}
          </motion.div>

          {/* LET'S GROW TOGETHER CTA banner */}
          <motion.div
            initial={{opacity:0, y:20}} whileInView={{opacity:1, y:0}} viewport={{once:true}}
            transition={{duration:.7, delay:.2}}
            className="mt-10 grid lg:grid-cols-2 gap-6 items-center"
            data-testid="hero-brand-quote"
          >
            <Link to="/contact"
              className="group inline-flex items-center gap-4 px-8 py-4 rounded-lg text-white font-bold tracking-wider"
              style={{
                background:"linear-gradient(90deg, #1D4ED8 0%, #06B6D4 100%)",
                clipPath:"polygon(0 0, 95% 0, 100% 50%, 95% 100%, 0 100%)",
                boxShadow:"0 12px 30px -10px rgba(6,182,212,0.5)",
              }}
              data-testid="hero-cta-grow"
            >
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="m22 2-7 20-4-9-9-4 20-7z"/>
              </svg>
              <span className="text-base sm:text-lg" style={{fontFamily:"Outfit"}}>LET&apos;S GROW TOGETHER!</span>
            </Link>
            <p className="text-blue-100/85 text-sm sm:text-base leading-relaxed">
              Ready to take your business to the next level?<br/>
              Let&apos;s build something <span className="text-cyan-300 font-semibold">amazing</span> together.
            </p>
          </motion.div>

          <div className="mt-12 flex justify-center">
            <div className="scroll-mouse" data-testid="scroll-indicator"/>
          </div>
        </div>
      </section>

      {/* Domain Search */}
      <section className="py-16 px-5" style={{background: 'var(--bg-section)'}} data-testid="home-domain-search">
        <div className="max-w-4xl mx-auto text-center">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full border border-amber-400/30 bg-amber-400/10 text-amber-300 text-xs font-semibold mb-3">
            <Globe2 size={12}/> Find your perfect domain
          </div>
          <h2 className="text-3xl sm:text-4xl font-extrabold text-white" style={{fontFamily:'Outfit'}}>
            Claim your brand in 30 seconds
          </h2>
          <p className="text-slate-400 mt-2">Search 100+ TLDs at the lowest prices in India. Free WHOIS privacy & SSL on every domain.</p>
          <div className="mt-7"><DomainSearch /></div>
        </div>
      </section>

      {/* AI Website Builder */}
      <HomeAIBuilder/>

      {/* SERVICES */}
      <section className="py-20 px-5" data-testid="services-section">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-14">
            <h2 className="text-4xl sm:text-5xl font-extrabold text-white" style={{fontFamily:'Outfit'}}>Our Services</h2>
            <p className="text-slate-400 mt-3 text-lg">Comprehensive digital solutions to accelerate your business growth</p>
          </div>
          <div className="grid sm:grid-cols-2 gap-6">
            {SERVICES.map((s, i) => (
              <motion.div
                key={s.title}
                initial={{opacity:0, y:20}} whileInView={{opacity:1, y:0}} viewport={{once:true}}
                transition={{duration:.4, delay:i*0.05}}
                className="card p-8"
              >
                <div className="icon-tile mb-5"><s.icon size={22}/></div>
                <h3 className="text-xl font-bold text-white mb-3" style={{fontFamily:'Outfit'}}>{s.title}</h3>
                <p className="text-slate-400 leading-relaxed">{s.desc}</p>
                <Link to={s.to} className="mt-5 inline-flex items-center gap-1.5 text-blue-400 hover:text-blue-300 text-sm font-semibold transition-colors">
                  Learn More <ArrowRight size={14}/>
                </Link>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* TRACK RECORD */}
      <section className="py-20 px-5" style={{background: 'var(--bg-section)'}} data-testid="track-record">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-14">
            <h2 className="text-4xl sm:text-5xl font-extrabold text-white" style={{fontFamily:'Outfit'}}>Our Track Record</h2>
            <p className="text-slate-400 mt-3 text-lg">Numbers that speak for our excellence</p>
          </div>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-6">
            {STATS.map((s, i) => (
              <motion.div
                key={s.label}
                initial={{opacity:0, y:20}} whileInView={{opacity:1, y:0}} viewport={{once:true}}
                transition={{duration:.4, delay:i*0.05}}
                className="card p-8 text-center"
              >
                <div className="w-14 h-14 mx-auto rounded-full bg-blue-500/10 border border-blue-500/30 flex items-center justify-center text-blue-400 mb-4">
                  <s.icon size={22}/>
                </div>
                <div className="text-4xl sm:text-5xl font-extrabold text-white" style={{fontFamily:'Outfit'}}>{s.value}</div>
                <div className="text-slate-400 mt-2 text-sm">{s.label}</div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* TESTIMONIALS */}
      <section className="py-20 px-5" data-testid="testimonials">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-14">
            <h2 className="text-4xl sm:text-5xl font-extrabold text-white" style={{fontFamily:'Outfit'}}>What Our Clients Say</h2>
            <p className="text-slate-400 mt-3 text-lg">Real results from real businesses we&apos;ve helped grow</p>
          </div>
          <div className="grid md:grid-cols-3 gap-6">
            {TESTIMONIALS.map((t, i) => (
              <motion.div
                key={t.name}
                initial={{opacity:0, y:20}} whileInView={{opacity:1, y:0}} viewport={{once:true}}
                transition={{duration:.4, delay:i*0.05}}
                className="card p-7"
              >
                <div className="flex justify-between items-start mb-5">
                  <Quote className="quote-icon" size={28} />
                  <div className="flex gap-0.5">
                    {Array.from({length:5}).map((_,j) => <Star key={j} size={15} className="star-gold" fill="currentColor" strokeWidth={0}/>)}
                  </div>
                </div>
                <p className="text-slate-200 leading-relaxed">&ldquo;{t.quote}&rdquo;</p>
                <div className="divider my-5"/>
                <div className="flex items-center gap-3">
                  <img src={t.avatar} alt={t.name} className="w-11 h-11 rounded-full object-cover ring-2 ring-blue-500/40" loading="lazy"/>
                  <div>
                    <div className="text-white font-semibold">{t.name}</div>
                    <div className="text-xs text-slate-400">{t.role}</div>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* FACEBOOK PAGE PLUGIN moved BELOW — see end of file */}

      {/* CTA / CONTACT FORM */}
      <section className="py-20 px-5" style={{background: 'var(--bg-section)'}} id="contact" data-testid="contact-section">
        <div className="max-w-2xl mx-auto">
          <div className="text-center mb-10">
            <h2 className="text-4xl sm:text-5xl font-extrabold text-white" style={{fontFamily:'Outfit'}}>Get Started Today</h2>
            <p className="text-slate-400 mt-3 text-lg">Let&apos;s discuss how we can help your business grow online</p>
          </div>
          <ContactForm />
        </div>
      </section>

      {/* FACEBOOK PAGE PLUGIN — placed right before the footer as the final
          social-conversion hook. Compact card with live follower count widget. */}
      <FacebookPagePlugin />
    </div>
  );
}


// ============================================================================
// FACEBOOK PAGE PLUGIN — embeds the official Facebook Page Plugin iframe
// which shows the realtime like/follow count, cover photo, and the most
// recent posts from facebook.com/Optivize.SM. No API key needed; FB
// rate-limits and refreshes the iframe content on their end.
// ============================================================================
function FacebookPagePlugin() {
  const FB_URL = "https://www.facebook.com/Optivize.SM";
  // Slim follow widget — shows live cover photo + follower count + Follow CTA.
  // No timeline; compact embed only.
  const FOLLOW_SRC =
    "https://www.facebook.com/plugins/page.php?" +
    "href=" + encodeURIComponent(FB_URL) +
    "&tabs=&width=380&height=160" +
    "&small_header=false&adapt_container_width=true" +
    "&hide_cover=false&show_facepile=true&appId=";

  return (
    <section className="py-14 px-5" data-testid="facebook-section">
      <div className="max-w-5xl mx-auto">
        <motion.div
          initial={{opacity:0, y:20}} whileInView={{opacity:1, y:0}} viewport={{once:true}}
          transition={{duration:.6}}
          className="card p-6 sm:p-8 grid lg:grid-cols-5 gap-6 items-center"
          data-testid="fb-compact-card"
        >
          {/* LEFT — branding + CTA */}
          <div className="lg:col-span-3">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-blue-600/15 border border-blue-500/30 text-blue-300 text-xs font-semibold tracking-wider mb-3">
              <svg width="13" height="13" viewBox="0 0 24 24" fill="currentColor"><path d="M22 12c0-5.52-4.48-10-10-10S2 6.48 2 12c0 4.84 3.44 8.87 8 9.8V15H8v-3h2V9.5C10 7.57 11.57 6 13.5 6H16v3h-2c-.55 0-1 .45-1 1v2h3v3h-3v6.95c5.05-.5 9-4.76 9-9.95z"/></svg>
              FOLLOW US ON FACEBOOK
            </div>
            <h3 className="text-2xl sm:text-3xl font-bold text-white" style={{fontFamily:'Outfit'}}>
              Join our growing community on Facebook
            </h3>
            <p className="text-slate-400 mt-2 text-sm sm:text-base">
              Real-time updates, design wins and tips from{" "}
              <span className="text-blue-300 font-semibold">@Optivize.SM</span>.
            </p>
            <a
              href={FB_URL} target="_blank" rel="noopener noreferrer"
              className="mt-5 inline-flex items-center gap-2 px-5 py-2.5 rounded-lg text-white font-semibold transition-transform hover:scale-[1.03]"
              style={{background:"#1877F2"}}
              data-testid="fb-visit-btn"
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor"><path d="M22 12c0-5.52-4.48-10-10-10S2 6.48 2 12c0 4.84 3.44 8.87 8 9.8V15H8v-3h2V9.5C10 7.57 11.57 6 13.5 6H16v3h-2c-.55 0-1 .45-1 1v2h3v3h-3v6.95c5.05-.5 9-4.76 9-9.95z"/></svg>
              Visit Our Facebook Page
            </a>
          </div>

          {/* RIGHT — compact follower count widget (live) */}
          <div className="lg:col-span-2">
            <div className="rounded-lg overflow-hidden bg-white" data-testid="fb-follow-widget">
              <iframe
                src={FOLLOW_SRC}
                width="380" height="160"
                style={{border:"none", overflow:"hidden", width:"100%", display:"block"}}
                scrolling="no" frameBorder="0"
                allow="autoplay; clipboard-write; encrypted-media; picture-in-picture; web-share"
                title="Optivize on Facebook — Follow"
                loading="lazy"
              />
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
}

